/**
 * components/quotes/QuoteLabel.tsx — Etichetta numerica di una quota (MUTA).
 *
 * Testo WebGL (`<Text>` drei/troika) dentro un `<Billboard>`: l'etichetta vive
 * NELLO spazio 3D come il resto della quota, quindi (a) rispetta depth test e
 * occlusione — sparisce dietro le travi invece di galleggiarci sopra — e (b)
 * viene catturata da `gl.render(...).toDataURL()` nell'export PDF. Entrambe
 * impossibili col precedente `<Html sprite>`, che era un nodo DOM sopra al
 * canvas e per costruzione sempre in primo piano (30-09-2026, richiesta
 * utente: "le quote devono essere elementi realmente presenti nella scena").
 *
 * Il chip di sfondo e' un piano opaco dimensionato sui `blockBounds` che
 * troika pubblica a sincronizzazione finita (`onSync`) e arretrato di
 * `labelChipDepthMm` per non entrare in z-fighting col testo.
 *
 * Colori e dimensione configurabili: default quota (`COLORS.quoteLabel*`,
 * `QUOTE_DEFAULTS.labelSizeMm`), riusato da `ForceArrow` coi propri
 * (`COLORS.forceLabel*`, `FORCE_ARROW_DEFAULTS.labelSizeMm`) — stesso pattern
 * di `Arrowhead.tsx`: default sovrascrivibili, non un componente duplicato.*/

import { Billboard, Text } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { useCallback, useRef, useState } from "react";
import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { QUOTE_DEFAULTS } from "../../config/defaults";
import type { Vec3 } from "../../data/types";

export interface QuoteLabelProps {
  /** Posizione mondo dell'etichetta (centro del testo).*/
  position: Vec3;
  /** Testo gia' formattato (es. "215 mm", "12.4°").*/
  text: string;
  /** Colore testo; default `COLORS.quoteLabelText`.*/
  textColor?: string;
  /** Colore sfondo chip; default `COLORS.quoteLabelBackground`.*/
  backgroundColor?: string;
  /** Altezza del carattere in mm mondo; default `QUOTE_DEFAULTS.labelSizeMm`.*/
  sizeMm?: number;
  /**
   * Direzione mondo in cui l'etichetta deve stare ACCANTO a `position`
   * (30-09-2026, quote lineari: `offsetDir`, l'esterno della quota). Ogni
   * frame la si proietta sugli assi schermo della camera e si sposta il
   * testo finche' il bordo del chip tocca `position`: vale per QUALUNQUE
   * vista (front, top, laterali, perspective) senza regole per asse.
   * Direzione (quasi) parallela all'asse di vista → nessuno spostamento.
   * Assente → etichetta centrata su `position`.*/
  awayFrom?: Vec3;
}

/** Riquadro di sfondo gia' dimensionato e centrato, in coordinate locali del billboard.*/
interface ChipBox {
  width: number;
  height: number;
  centerX: number;
  centerY: number;
}

/** Etichetta billboard in spazio 3D (occlude e si cattura), colori/dimensione da config.*/
export function QuoteLabel({
  position,
  text,
  textColor = COLORS.quoteLabelText,
  backgroundColor = COLORS.quoteLabelBackground,
  sizeMm = QUOTE_DEFAULTS.labelSizeMm,
  awayFrom,
}: QuoteLabelProps) {
  const [chip, setChip] = useState<ChipBox | null>(null);
  const shiftRef = useRef<THREE.Group>(null);

  const handleSync = useCallback(
    (mesh: unknown) => setChip((previous) => nextChip(previous, mesh, sizeMm)),
    [sizeMm],
  );

  // Il Billboard copia l'orientamento della camera: nel suo frame locale X e'
  // la destra dello schermo e Y il su, quindi lo spostamento si calcola li'.
  useFrame(({ camera }) => {
    const group = shiftRef.current;
    if (!group) return;
    const shift = awayFrom && chip ? labelShift(awayFrom, chip, camera.quaternion) : null;
    if (shift) group.position.set(shift[0], shift[1], 0);
    else group.position.set(0, 0, 0);
  });

  return (
    <Billboard position={position}>
      <group ref={shiftRef}>
        {chip ? (
          <mesh position={[chip.centerX, chip.centerY, -QUOTE_DEFAULTS.labelChipDepthMm]}>
            <planeGeometry args={[chip.width, chip.height]} />
            <meshBasicMaterial color={backgroundColor} />
          </mesh>
        ) : null}
        <Text
          font={QUOTE_DEFAULTS.labelFontUrl}
          fontSize={sizeMm}
          color={textColor}
          anchorX="center"
          anchorY="middle"
          onSync={handleSync}
        >
          {text}
        </Text>
      </group>
    </Billboard>
  );
}

//─── Spostamento dell'etichetta accanto al punto (puro) ──────────

/** Sotto questa componente sullo schermo la direzione e' "lungo l'asse di vista": nessuno spostamento.*/
const MIN_SCREEN_COMPONENT = 0.2;
const SCREEN_RIGHT = new THREE.Vector3();
const SCREEN_UP = new THREE.Vector3();
const AWAY = new THREE.Vector3();

/**
 * Spostamento (frame locale del billboard) che porta il chip interamente dal
 * lato `awayFrom` di `position`, a filo: distanza = funzione di supporto del
 * rettangolo nella direzione schermo (|dx|·w/2 + |dy|·h/2), piu' il
 * ricentraggio sul centro dei `blockBounds`.*/
function labelShift(awayFrom: Vec3, chip: ChipBox, cameraQuaternion: THREE.Quaternion): [number, number] | null {
  SCREEN_RIGHT.set(1, 0, 0).applyQuaternion(cameraQuaternion);
  SCREEN_UP.set(0, 1, 0).applyQuaternion(cameraQuaternion);
  AWAY.set(awayFrom[0], awayFrom[1], awayFrom[2]);
  let dx = AWAY.dot(SCREEN_RIGHT);
  let dy = AWAY.dot(SCREEN_UP);
  const length = Math.hypot(dx, dy);
  if (length < MIN_SCREEN_COMPONENT) return null;
  dx /= length;
  dy /= length;
  const distance = (Math.abs(dx) * chip.width) / 2 + (Math.abs(dy) * chip.height) / 2;
  return [dx * distance - chip.centerX, dy * distance - chip.centerY];
}

//─── Derivazione pura del riquadro di sfondo ─────────────────────

/**
 * Riquadro dai `blockBounds` di troika ([minX, minY, maxX, maxY], gia' in
 * coordinate locali post-anchor). Ritorna il valore PRECEDENTE quando non
 * cambia: `onSync` scatta a ogni risincronizzazione e un nuovo oggetto a
 * ogni giro rimonterebbe il piano senza motivo.*/
function nextChip(previous: ChipBox | null, mesh: unknown, sizeMm: number): ChipBox | null {
  const bounds = (mesh as { textRenderInfo?: { blockBounds?: ArrayLike<number> } } | null)
    ?.textRenderInfo?.blockBounds;
  if (!bounds || bounds.length < 4) return previous;

  const minX = bounds[0] ?? 0;
  const minY = bounds[1] ?? 0;
  const maxX = bounds[2] ?? 0;
  const maxY = bounds[3] ?? 0;
  const padding = sizeMm * QUOTE_DEFAULTS.labelChipPaddingRatio;

  const next: ChipBox = {
    width: maxX - minX + 2 * padding,
    height: maxY - minY + 2 * padding,
    centerX: (minX + maxX) / 2,
    centerY: (minY + maxY) / 2,
  };
  if (next.width <= 0 || next.height <= 0) return previous;
  return previous && sameChip(previous, next) ? previous : next;
}

const CHIP_EPSILON_MM = 1e-3;

/** Uguaglianza a meno del micron: evita re-render su rumore numerico di troika.*/
function sameChip(a: ChipBox, b: ChipBox): boolean {
  return (
    Math.abs(a.width - b.width) < CHIP_EPSILON_MM &&
    Math.abs(a.height - b.height) < CHIP_EPSILON_MM &&
    Math.abs(a.centerX - b.centerX) < CHIP_EPSILON_MM &&
    Math.abs(a.centerY - b.centerY) < CHIP_EPSILON_MM
  );
}
